// W21 Repuestos: every pending part across the open orders, with its
// supplier, expected date and status. When all parts of a car arrived the
// page suggests moving it on (same action as the board).
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Package, Pencil, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useConfig, useOrdenesAbiertas } from '@/datos/consultas';
import type { OrdenDoc } from '@/datos/tipos';
import { permisos, useSesionActiva } from '@/lib/sesion';
import { useAhora } from '@/lib/tiempo';
import { DialogoRepuestos } from '@/repuestos/DialogoRepuestos';
import { InsigniaRepuesto, repuestoAtrasado } from '@/repuestos/estado';
import { filtrarRepuestos, vistas, type Vista } from '@/repuestos/filtro';
import { Encabezado } from '@/shell/Encabezado';
import { useOrdenesVisibles } from '@/tablero/movimientos';
import { useAccionesOrden } from '@/tablero/useAccionesOrden';
import { ErrorCarga, Esqueleto, Segmentado, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { Campo, Selector } from '@/ui/campo';
import { cn } from '@/ui/cn';
import { Dialogo, DialogoContenido, DialogoCuerpo, DialogoPie } from '@/ui/dialogo';
import { Contenedor } from '@/ui/pagina';
import { formatearPatente, TextoPatente } from '@/ui/patente';

export const Route = createFileRoute('/_app/repuestos')({
  validateSearch: z.object({
    ver: z.enum(vistas).optional().catch(undefined),
    q: z.string().max(40).optional().catch(undefined),
  }),
  component: Pagina,
});

function Pagina() {
  const { ver = 'pendientes', q = '' } = Route.useSearch();
  const navegar = useNavigate({ from: Route.fullPath });
  const s = useSesionActiva();
  const ahora = useAhora();
  const config = useConfig();
  const servidor = useOrdenesAbiertas();
  const ordenes = useOrdenesVisibles(servidor.data);
  const fases = useMemo(() => config.data?.fases ?? [], [config.data]);
  const acciones = useAccionesOrden(ordenes, fases);
  const editable = permisos.editarRepuestos(s.rol);
  const [texto, setTexto] = useState(q);
  const [editar, setEditar] = useState<{
    orden: OrdenDoc;
    nuevo: boolean;
    n: number;
  } | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [elegir, setElegir] = useState(false);

  const lista = useMemo(() => ordenes ?? [], [ordenes]);
  const filas = useMemo(() => filtrarRepuestos(lista, ver, q, ahora), [lista, ver, q, ahora]);
  const conRepuestos = lista.filter((o) => o.repuestos?.length);
  const pendientes = lista.flatMap((o) => o.repuestos ?? []).filter((r) => r.estado !== 'recibido');
  const atrasados = pendientes.filter((r) => repuestoAtrasado(r, ahora)).length;
  const retenidos = lista.filter((o) => o.retencion === 'retenido').length;
  const idxEspera = fases.findIndex((f) => f.id === 'esperando_repuestos');
  const listos = lista.filter(
    (o) =>
      !o.enZonaNeutral &&
      o.faseIdx === idxEspera &&
      (o.repuestos?.length ?? 0) > 0 &&
      o.repuestos.every((r) => r.estado === 'recibido'),
  );

  const abrir = (orden: OrdenDoc, nuevo = false) => {
    setEditar((e) => ({ orden, nuevo, n: (e?.n ?? 0) + 1 }));
    setAbierto(true);
  };
  const fijar = (c: { ver?: Vista | undefined; q?: string | undefined }) =>
    void navegar({ search: (p) => ({ ...p, ...c }), replace: true });

  return (
    <>
      <Encabezado
        titulo="Repuestos"
        ayuda="repuestos"
        subtitulo={
          ordenes
            ? `${retenidos} ${retenidos === 1 ? 'vehículo retenido' : 'vehículos retenidos'} esperando repuestos · ${pendientes.length} por llegar`
            : 'Cargando…'
        }
        acciones={
          editable ? (
            <Boton
              variante="primario"
              onClick={() => setElegir(true)}
              aria-label="Registrar pedido"
              className="max-sm:size-11 max-sm:px-0"
            >
              <Plus /> <span className="max-sm:hidden">Registrar pedido</span>
            </Boton>
          ) : undefined
        }
      />
      <Contenedor>
        {listos.length > 0 && (
          <Tarjeta className="flex flex-col gap-3 border-sin-strong/40 bg-sin-soft/40 p-4 sm:p-5">
            <p className="text-[14px] font-bold text-sin-texto">
              {listos.length === 1
                ? '1 auto tiene todos sus repuestos'
                : `${listos.length} autos tienen todos sus repuestos`}
              : puede pasar a {fases[idxEspera + 1]?.nombre ?? 'la siguiente fase'}.
            </p>
            <ul className="flex flex-wrap gap-2">
              {listos.map((o) => (
                <li
                  key={o.id}
                  className="flex items-center gap-3 rounded-[12px] border border-border bg-surface py-1.5 pr-1.5 pl-3"
                >
                  <TextoPatente patente={o.patente} className="text-[16px]" />
                  <span className="max-w-[180px] truncate text-[13px] text-text-2">
                    {o.vehiculo}
                  </span>
                  {permisos.moverAutos(s.rol) && (
                    <Boton tamano="sm" variante="suave" onClick={() => acciones.avanzar(o)}>
                      Pasar <ArrowRight />
                    </Boton>
                  )}
                </li>
              ))}
            </ul>
          </Tarjeta>
        )}

        <div className="flex flex-wrap items-center gap-2.5">
          <Segmentado
            etiqueta="Mostrar"
            valor={ver}
            onCambio={(v) => fijar({ ver: v === 'pendientes' ? undefined : v })}
            opciones={[
              {
                valor: 'pendientes',
                texto: 'Por llegar',
                cantidad: pendientes.length,
              },
              { valor: 'atrasados', texto: 'Atrasados', cantidad: atrasados },
              { valor: 'disponibles', texto: 'Disponibles' },
              { valor: 'todos', texto: 'Todos' },
            ]}
            className="max-w-full overflow-x-auto"
          />
          <label className="relative max-sm:w-full">
            <span className="sr-only">Buscar repuesto</span>
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-text-2" />
            <input
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                fijar({ q: e.target.value.trim() || undefined });
              }}
              placeholder="Patente, repuesto o proveedor"
              className="h-[38px] w-full rounded-[10px] border border-border-input bg-surface pr-3 pl-9 text-[14px] focus:border-primary focus:shadow-[0_0_0_1px_var(--tm-primary),0_0_0_4px_var(--tm-primary-soft)] focus:outline-none sm:w-[260px]"
            />
          </label>
        </div>

        {servidor.isError ? (
          <ErrorCarga error={servidor.error} />
        ) : !ordenes ? (
          <Tarjeta className="flex flex-col gap-3 p-5" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <Esqueleto key={i} className="h-10" />
            ))}
          </Tarjeta>
        ) : filas.length === 0 ? (
          <Tarjeta>
            <Vacio
              icono={<Package />}
              titulo={
                q
                  ? 'Ningún repuesto coincide con la búsqueda'
                  : ver === 'atrasados'
                    ? 'Ningún repuesto atrasado'
                    : conRepuestos.length === 0
                      ? 'Ningún auto espera repuestos'
                      : 'Nada en esta vista'
              }
            >
              {conRepuestos.length === 0 && !q
                ? 'Cuando un auto necesite piezas, regístralas con “Registrar pedido”.'
                : undefined}
            </Vacio>
          </Tarjeta>
        ) : (
          <>
            <Tarjeta className="hidden overflow-hidden md:block">
              <table className="w-full table-fixed text-[14px]">
                <caption className="sr-only">Repuestos de los autos en el taller</caption>
                <thead>
                  <tr className="text-left text-[12px] tracking-[0.06em] text-text-2 uppercase">
                    <th
                      scope="col"
                      className="h-11 w-[130px] border-b border-text/80 pl-5 font-bold"
                    >
                      Patente
                    </th>
                    <th scope="col" className="border-b border-text/80 px-3 font-bold">
                      Repuesto
                    </th>
                    <th scope="col" className="w-[190px] border-b border-text/80 px-3 font-bold">
                      Proveedor
                    </th>
                    <th scope="col" className="w-[210px] border-b border-text/80 px-3 font-bold">
                      Estado
                    </th>
                    <th scope="col" className="w-[64px] border-b border-text/80 pr-4">
                      <span className="sr-only">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map(({ orden, r, n }) => (
                    <tr key={`${orden.id}-${n}`} className="group hover:bg-surface-muted/60">
                      <td className="h-[52px] border-b border-border pl-5">
                        <Link
                          to="/vehiculos/$patente"
                          params={{ patente: orden.patente }}
                          className="hover:text-primary-text"
                          aria-label={`Ficha de ${formatearPatente(orden.patente)}`}
                        >
                          <TextoPatente patente={orden.patente} className="text-[17px]" />
                        </Link>
                      </td>
                      <td className="max-w-0 border-b border-border px-3">
                        <span
                          className={cn(
                            'block truncate font-bold',
                            repuestoAtrasado(r, ahora) && 'text-atraso-texto',
                          )}
                        >
                          {r.nombre}
                        </span>
                        <span className="block truncate text-[12.5px] text-text-2">
                          {orden.vehiculo}
                          {orden.retencion === 'retenido'
                            ? ' · retenido en el taller'
                            : orden.retencion === 'devuelto'
                              ? ' · devuelto al cliente'
                              : ''}
                        </span>
                      </td>
                      <td className="max-w-0 truncate border-b border-border px-3 text-text-long">
                        {r.proveedor ?? <span className="text-text-2">—</span>}
                      </td>
                      <td className="border-b border-border px-3">
                        <InsigniaRepuesto r={r} ahora={ahora} />
                      </td>
                      <td className="border-b border-border pr-4 text-right">
                        {editable && (
                          <Boton
                            tamano="iconoSm"
                            variante="fantasma"
                            onClick={() => abrir(orden)}
                            aria-label={`Editar repuestos de ${formatearPatente(orden.patente)}`}
                          >
                            <Pencil />
                          </Boton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Tarjeta>

            <ul className="flex flex-col gap-2.5 md:hidden">
              {filas.map(({ orden, r, n }) => (
                <li
                  key={`${orden.id}-${n}`}
                  className="flex items-center gap-3 rounded-[14px] border border-border bg-surface px-3.5 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-baseline gap-2">
                      <TextoPatente patente={orden.patente} className="text-[16px]" />
                      <span
                        className={cn(
                          'truncate text-[14px] font-bold',
                          repuestoAtrasado(r, ahora) && 'text-atraso-texto',
                        )}
                      >
                        {r.nombre}
                      </span>
                    </p>
                    <p className="mt-0.5 truncate text-[13px] text-text-2">
                      {r.proveedor ?? 'Sin proveedor'}
                    </p>
                    <div className="mt-1.5">
                      <InsigniaRepuesto r={r} ahora={ahora} />
                    </div>
                  </div>
                  {editable && (
                    <Boton
                      tamano="icono"
                      variante="fantasma"
                      onClick={() => abrir(orden)}
                      aria-label={`Editar repuestos de ${formatearPatente(orden.patente)}`}
                    >
                      <Pencil />
                    </Boton>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-[13px] text-text-2">
          Cuando todos los repuestos de un auto están “Disponible”, aparece arriba para pasarlo a la
          siguiente fase.
        </p>
      </Contenedor>

      {editar && (
        <DialogoRepuestos
          key={`rep-${editar.n}`}
          orden={lista.find((o) => o.id === editar.orden.id) ?? editar.orden}
          agregarUno={editar.nuevo}
          abierto={abierto}
          onCambio={setAbierto}
        />
      )}
      <DialogoElegirAuto
        abierto={elegir}
        onCambio={setElegir}
        ordenes={lista}
        onElegir={(o) => {
          setElegir(false);
          abrir(o, true);
        }}
      />
      {acciones.dialogos}
    </>
  );
}

function DialogoElegirAuto({
  abierto,
  onCambio,
  ordenes,
  onElegir,
}: {
  abierto: boolean;
  onCambio: (v: boolean) => void;
  ordenes: OrdenDoc[];
  onElegir: (o: OrdenDoc) => void;
}) {
  const [id, setId] = useState('');
  const ordenadas = [...ordenes].sort((a, b) => a.patente.localeCompare(b.patente));
  return (
    <Dialogo open={abierto} onOpenChange={onCambio}>
      <DialogoContenido
        titulo="Registrar pedido de repuestos"
        descripcion="Elige el auto; después agregas los repuestos con su proveedor y fecha de llegada."
        ancho="sm"
      >
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            const o = ordenes.find((x) => x.id === id);
            if (o) onElegir(o);
          }}
        >
          <DialogoCuerpo>
            <Campo etiqueta="Auto en el taller">
              {(p) => (
                <Selector {...p} value={id} onChange={(e) => setId(e.target.value)} required>
                  <option value="" disabled>
                    Elige una patente
                  </option>
                  {ordenadas.map((o) => (
                    <option key={o.id} value={o.id}>
                      {formatearPatente(o.patente)} · {o.vehiculo}
                    </option>
                  ))}
                </Selector>
              )}
            </Campo>
          </DialogoCuerpo>
          <DialogoPie>
            <Boton onClick={() => onCambio(false)}>Cancelar</Boton>
            <Boton type="submit" variante="primario" disabled={!id}>
              Continuar
            </Boton>
          </DialogoPie>
        </form>
      </DialogoContenido>
    </Dialogo>
  );
}
